import { useState, useMemo, useCallback, useEffect } from 'react';
import { NPSDSearchFilters } from '@/components/npsd/NPSDSearchFilters';
import { NPSDSearchResults } from '@/components/npsd/NPSDSearchResults';
import { Dropdownlist } from '@/components/Dropdownlist';
import { useNPSDSearch, NPSDSearchFilter } from '@/hooks/useNPSDSearch';

import { Codes } from '@/constants/codes';

interface NPSDSearchVehicleProps {
  searchMode?: string;
  isMobile?: boolean;
  theme?: string;
  initialValues?: Record<string, any>;
  showNewButton?: boolean;
  onView?: (item: any) => void;
  onAdd?: (item: any) => void;
  onLink?: (item: any) => void;
  onNew?: () => void;
}

export function NPSDSearchVehicle({
  searchMode = 'default',
  isMobile = false,
  theme = '',
  initialValues = {},
  showNewButton = false,
  onView,
  onAdd,
  onLink,
  onNew,
}: NPSDSearchVehicleProps) {
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [color, setColor] = useState('');
  const [plateNumber, setPlateNumber] = useState('');
  const [plateState, setPlateState] = useState<string | null>('');
  const [vin, setVin] = useState('');

  const [states, setStates] = useState<any[]>([]);

  const {
    results, totalRecords, currentPage, pageSize, isLoading, hasSearched,
    showViewButton, showAddButton, showLinkButton,
    performSearch, clearResults, handlePageChange, handlePageSizeChange,
  } = useNPSDSearch({ entityApiName: 'Vehicle', searchMode });

  const columns = useMemo(() => [
    { key: 'plateNumber', label: 'Plate Number', field: 'plateNumber' },
    { key: 'plateState', label: 'Plate State', field: 'plateState' },
    { key: 'make', label: 'Make', field: 'make' },
    { key: 'model', label: 'Model', field: 'model' },
    { key: 'color', label: 'Color', field: 'color' },
    { key: 'vin', label: 'VIN', field: 'vin', fullWidth: true },
  ], []);

  const stateItems = useMemo(() => {
    return states.map((s: any) => ({ label: s.code, value: s.code }));
  }, [states]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (make) count++;
    if (model) count++;
    if (color) count++;
    if (plateNumber) count++;
    if (plateState) count++;
    if (vin) count++;
    return count;
  }, [make, model, color, plateNumber, plateState, vin]);

  // Load states on mount
  useEffect(() => {
    if (typeof Codes !== 'undefined' && typeof Codes.getStates === 'function') {
      setStates(Codes.getStates() || []);
    }
  }, []);

  // Apply initial values
  useEffect(() => {
    if (initialValues) {
      if (initialValues.make) setMake(initialValues.make);
      if (initialValues.model) setModel(initialValues.model);
      if (initialValues.color) setColor(initialValues.color);
      if (initialValues.plateNumber) setPlateNumber(initialValues.plateNumber);
      if (initialValues.plateState) setPlateState(initialValues.plateState);
      if (initialValues.vin) setVin(initialValues.vin);
    }
  }, [initialValues]);

  const buildFilters = useCallback((): NPSDSearchFilter[] => {
    const filters: NPSDSearchFilter[] = [];

    if (make) {
      filters.push({ field: 'make', operator: 'contains', value: make.trim() });
    }
    if (model) {
      filters.push({ field: 'model', operator: 'contains', value: model.trim() });
    }
    if (color) {
      filters.push({ field: 'color', operator: 'contains', value: color.trim() });
    }
    if (plateNumber) {
      filters.push({ field: 'plateNumber', operator: 'contains', value: plateNumber.trim() });
    }
    if (plateState) {
      filters.push({ field: 'plateState', operator: 'contains', value: plateState.trim() });
    }
    if (vin) {
      filters.push({ field: 'vin', operator: 'contains', value: vin.trim() });
    }

    return filters;
  }, [make, model, color, plateNumber, plateState, vin]);

  const doSearch = useCallback(() => {
    performSearch(buildFilters());
  }, [performSearch, buildFilters]);

  const clearSearch = useCallback(() => {
    setMake('');
    setModel('');
    setColor('');
    setPlateNumber('');
    setPlateState('');
    setVin('');
    clearResults();
  }, [clearResults]);

  const handleView = useCallback((item: any) => {
    onView?.(item);
  }, [onView]);

  const handleAdd = useCallback((item: any) => {
    onAdd?.(item);
  }, [onAdd]);

  const handleLink = useCallback((item: any) => {
    onLink?.(item);
  }, [onLink]);

  const handleNew = useCallback(() => {
    onNew?.();
  }, [onNew]);

  const handleRowClick = useCallback((item: any) => {
    if (searchMode === 'default') {
      handleView(item);
    } else if (showAddButton) {
      handleAdd(item);
    } else if (showLinkButton) {
      handleLink(item);
    }
  }, [searchMode, showAddButton, showLinkButton, handleView, handleAdd, handleLink]);

  const onPageChange = useCallback((page: number) => {
    handlePageChange(page, buildFilters());
  }, [handlePageChange, buildFilters]);

  const onPageSizeChange = useCallback((size: number) => {
    handlePageSizeChange(size, buildFilters());
  }, [handlePageSizeChange, buildFilters]);

  const getDisplayInfo = useCallback((item: any): string => {
    const parts: string[] = [];
    if (item.plateNumber) parts.push(item.plateNumber);
    if (item.plateState) parts.push(item.plateState);
    return parts.join(' - ') || 'Unknown Vehicle';
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      doSearch();
    }
  }, [doSearch]);

  const renderCardTitle = useCallback((item: any) => getDisplayInfo(item), [getDisplayInfo]);

  return (
    <div className={`npsd-search-vehicle ${theme}`}>
      <NPSDSearchFilters
        activeFilterCount={activeFilterCount}
        loading={isLoading}
        onSearch={doSearch}
      >
        <div className="search-fields">
          <div className="field-row">
            <div className="field">
              <label>Make</label>
              <input
                type="text"
                value={make}
                onChange={(e) => setMake(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter Make"
              />
            </div>
            <div className="field">
              <label>Model</label>
              <input
                type="text"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter Model"
              />
            </div>
            <div className="field">
              <label>Color</label>
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter Color"
              />
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>Plate</label>
              <input
                type="text"
                value={plateNumber}
                onChange={(e) => setPlateNumber(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter Plate"
              />
            </div>
            <div className="field">
              <label>Plate State</label>
              <Dropdownlist
                value={plateState}
                items={stateItems}
                labelKey="label"
                valueKey="value"
                placeholder="Select State"
                clearable={true}
                onChange={(val) => setPlateState(val as string | null)}
              />
            </div>
            <div className="field">
              <label>VIN</label>
              <input
                type="text"
                value={vin}
                onChange={(e) => setVin(e.target.value)}
                maxLength={17}
                onKeyDown={handleKeyDown}
                placeholder="Enter VIN"
              />
            </div>
          </div>
        </div>
        <div className="search-actions">
          <button type="button" className="btn btn-primary" onClick={doSearch} disabled={isLoading}>
            <i className="fa fa-search"></i> Search
          </button>
          <button type="button" className="btn btn-secondary" onClick={clearSearch}>
            <i className="fa fa-trash"></i> Clear
          </button>
          {showNewButton && (
            <button type="button" className="btn btn-success" onClick={handleNew}>
              <i className="fa fa-plus"></i> New
            </button>
          )}
        </div>
      </NPSDSearchFilters>

      {/* Results */}
      <NPSDSearchResults
        columns={columns}
        results={results}
        loading={isLoading}
        hasSearched={hasSearched}
        totalRecords={totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
        renderCardTitle={renderCardTitle}
        onRowClick={handleRowClick}
        onLinkClick={handleView}
        onCurrentPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
}

(window as any).NPSDSearchVehicleReact = NPSDSearchVehicle;
